import { redirect } from "react-router";
import { NyvaError } from "../errors/errors";
import { parseLanguage } from "../translation/useRootPath";
import { clear } from "./cache";

// In case of HTTP response outside of 200-299 range
async function getHTTPError(response: Response): Promise<Error> {
  const error = new NyvaError(
    `${response.status}: ${response.statusText}`,
    response.status,
    false,
    "",
  );

  try {
    const contentType = response.headers.get("content-type");

    if (contentType && contentType.indexOf("text/plain") !== -1) {
      const apiErrorText = await response.text();
      error.apiError = apiErrorText;
    } else {
      const apiErrorJson = await response.json();
      if (apiErrorJson) {
        error.apiError = apiErrorJson["error-code"] || apiErrorJson.code || "";
        error.apiErrorMessage =
          apiErrorJson["error-message"] || apiErrorJson.message || "";
      }
    }
  } catch (errorProcessingErrorContent: unknown) {
    if (errorProcessingErrorContent instanceof Error) {
      error.apiError = errorProcessingErrorContent.name;
      error.apiErrorMessage = errorProcessingErrorContent.message;
    }
  }

  return error;
}

function loginPath(): string {
  return `/${parseLanguage(window.location.pathname.split("/")[1])}/login`;
}

async function getErrorOrRedirect(response: Response): Promise<unknown> {
  if (response.status === 401 && window.location.pathname !== loginPath()) {
    clear();
    return redirect(loginPath());
  }

  return getHTTPError(response);
}

export async function get(uri: string): Promise<Response> {
  const options: RequestInit = {
    credentials: "same-origin",
    headers: {},
  };

  const response = await fetch(uri, options);

  if (response.ok) {
    return response;
  } else {
    throw await getErrorOrRedirect(response);
  }
}

export async function getJSON<T>(uri: string): Promise<T> {
  return (await get(uri)).json();
}

export async function getPossibleUndefinedJSON<T>(
  uri: string,
): Promise<T | undefined> {
  try {
    return await getJSON<T>(uri);
  } catch (error) {
    if (error instanceof NyvaError && error.statusCode === 404) {
      return undefined;
    }
    throw error;
  }
}

export async function request(
  method: string,
  uri: string,
  body: unknown,
  csrfToken?: string,
  extraHeaders?: Record<string, string>,
): Promise<Response> {
  const headers: HeadersInit = {
    Accept: "application/hal+json;q=1.0, application/json;q=0.9",
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
    ...extraHeaders,
  };
  if (csrfToken) {
    headers["X-CSRF-TOKEN"] = csrfToken;
  }

  const options: RequestInit = {
    method: method,
    credentials: "same-origin",
    body: typeof body === "string" ? body : JSON.stringify(body),
    headers,
  };

  const response = await fetch(uri, options);

  if (response.ok) {
    return response;
  } else {
    throw await getErrorOrRedirect(response);
  }
}

export function post(
  uri: string,
  body: unknown,
  csrfToken?: string,
): Promise<Response> {
  return request("POST", uri, body, csrfToken);
}

export function remove(
  uri: string,
  csrfToken: string,
  body: unknown = {},
  extraHeaders?: Record<string, string>,
): Promise<Response> {
  return request("DELETE", uri, body, csrfToken, extraHeaders);
}

export async function getHalJSON<T>(uri: string): Promise<T> {
  const options: RequestInit = {
    credentials: "same-origin",
    headers: {
      Accept: "application/hal+json",
    },
  };

  const response = await fetch(uri, options);

  if (response.ok) {
    return response.json();
  } else {
    throw await getErrorOrRedirect(response);
  }
}
